import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { VictoryArea, VictoryAxis, VictoryChart, VictoryLine } from 'victory';

import { Button } from 'components/Form';
import LoadingDots from 'components/LoadingDots';
import Message from 'components/Message';
import { TooltipContent, TooltipToggle } from 'components/Tooltip';
import { ICONS } from 'constants/icons';
import { schedulePropTypes } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';
import { scrollToTop } from 'helpers/page-utils';
import Segment from 'helpers/segment';
import { selectActiveVenueId } from 'selectors/general';

import { SCHEDULE_LIST_V2_FILL_BAR_VARIANT, getScheduleListV2FillBar } from '../ScheduleListV2/ScheduleListV2.helpers';
import {
  DIRECT_BOOKINGS_CHART_DAYS,
  DIRECT_BOOKINGS_COPY,
  getDirectBookingsChart,
  getDirectBookingsCopyVariant,
  hasFillRateLoadError,
} from './ScheduleDetailV2.helpers';
import { useScheduleFillRate } from './ScheduleDetailV2.hooks';
import messages from './ScheduleDetailV2.messages';
import './DirectBookingsSection.scss';

const { block, element } = bem('direct-bookings-section');

// Victory draws inline SVG styles, so the Figma tokens are mirrored as hex constants
// (same approach as SmartSpotFillChartTheme).
const COLOR_BLACK = '#000000';
const COLOR_GRAY20 = '#efefef';
const COLOR_GRAY50 = '#bdbdbd';
const COLOR_GRAY70 = '#676767';
// lighten($color-brand, 40), the direct-bookings color the shared fill bar paints.
const COLOR_DIRECT = '#ccddff';
const COLOR_CURRENT_AREA = 'rgba(204, 221, 255, 0.24)';
const FONT_SIZE_TINY = 12;
const FONT_SIZE_SMALL = 14;
const FONT_WEIGHT_REGULAR = 400;
const FONT_WEIGHT_MEDIUM = 500;
const SPACING_TINY = 4;
const SPACING_SMALL = 8;
const SPACING_REGULAR = 16;
const CHART_HEIGHT_IN_PX = 286;
// Figma content width; Victory scales its SVG to the container, so the width must track the real
// element for fonts and strokes to render at 1:1.
const CHART_DEFAULT_WIDTH_IN_PX = 606;
const CHART_PADDING = { bottom: 50, left: 28, right: 8, top: 32 };
const CHART_THEME = {
  axis: {
    style: {
      axis: { stroke: 'none' },
      axisLabel: {
        fill: COLOR_GRAY70,
        fontFamily: 'inherit',
        fontSize: FONT_SIZE_SMALL,
        fontWeight: FONT_WEIGHT_MEDIUM,
        padding: SPACING_REGULAR * 2 + SPACING_TINY,
      },
      grid: { stroke: COLOR_GRAY20, strokeWidth: 1 },
      tickLabels: {
        fill: COLOR_BLACK,
        fontFamily: 'inherit',
        fontSize: FONT_SIZE_TINY,
        fontWeight: FONT_WEIGHT_REGULAR,
        padding: SPACING_SMALL,
      },
      ticks: { size: 0 },
    },
  },
};
const DASHED = '4,4';
const DOTTED = '2,2';
// Figma starts each day label at the left edge of six equal columns inside an 8px edge padding and
// pins a 12px "1d" to the right edge, so the labels are evenly spaced rather than at exact day marks.
const X_AXIS_EDGE_PADDING = 8;
const X_AXIS_LABEL_WIDTH = 12;
const X_AXIS_COLUMNS = DIRECT_BOOKINGS_CHART_DAYS - 1;
const Y_AXIS_SEGMENTS = 3;
const BODY_MESSAGE_BY_VARIANT = {
  [DIRECT_BOOKINGS_COPY.AT_OR_ABOVE]: messages.directBookingsBodyAtOrAbove,
  [DIRECT_BOOKINGS_COPY.BELOW]: messages.directBookingsBodyBelow,
  [DIRECT_BOOKINGS_COPY.ERROR]: messages.directBookingsBodyError,
  [DIRECT_BOOKINGS_COPY.NO_HISTORY]: messages.directBookingsBodyNoHistory,
};
const PREDICTION_TOOLTIP_NAME_PREFIX = 'direct-bookings-prediction';
const SEGMENT_MANAGE_SMARTSPOT_CLICKED = 'manage_smartspot_clicked';
const SEGMENT_ORIGIN = 'insights_chart';

const chartPointPropType = PropTypes.shape({
  daysBefore: PropTypes.number,
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
});

function getXAxisTicks(plotWidth) {
  const columnWidth = (plotWidth - X_AXIS_EDGE_PADDING * 2 - X_AXIS_LABEL_WIDTH) / X_AXIS_COLUMNS;
  const toDays = px => (px / plotWidth) * DIRECT_BOOKINGS_CHART_DAYS;
  return Array.from({ length: X_AXIS_COLUMNS + 1 }, (_, index) =>
    toDays(X_AXIS_EDGE_PADDING + X_AXIS_LABEL_WIDTH / 2 + columnWidth * index)
  );
}

function getYAxisTicks(yMax) {
  const ticks = Array.from({ length: Y_AXIS_SEGMENTS + 1 }, (_, index) => Math.round((yMax * index) / Y_AXIS_SEGMENTS));
  return ticks.filter((tick, index) => index === 0 || tick > ticks[index - 1]);
}

function useElementWidth(defaultWidth) {
  const ref = useRef(null);
  const [width, setWidth] = useState(defaultWidth);

  useEffect(() => {
    if (!ref.current || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(([entry]) => {
      const nextWidth = Math.round(entry.contentRect.width);
      if (nextWidth > 0) {
        setWidth(nextWidth);
      }
    });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}

function DirectBookingsChart({ chart, muted }) {
  const intl = useIntl();
  const [containerRef, chartWidth] = useElementWidth(CHART_DEFAULT_WIDTH_IN_PX);
  const { current, pastAvg, prediction, today, yMax } = chart;
  const yTicks = getYAxisTicks(yMax);
  const plotWidth = chartWidth - CHART_PADDING.left - CHART_PADDING.right;
  const xTicks = getXAxisTicks(plotWidth);
  const yDomainMax = yTicks[yTicks.length - 1];
  const todayLeft = CHART_PADDING.left + (today.x / DIRECT_BOOKINGS_CHART_DAYS) * plotWidth;

  return (
    <div
      className={element('chart', { muted })}
      data-cypress="schedule-detail-v2-direct-bookings-chart"
      ref={containerRef}>
      <VictoryChart
        width={chartWidth}
        domain={{ x: [0, DIRECT_BOOKINGS_CHART_DAYS], y: [0, yDomainMax] }}
        height={CHART_HEIGHT_IN_PX}
        padding={CHART_PADDING}
        theme={CHART_THEME}>
        <VictoryAxis
          label={intl.formatMessage(messages.directBookingsDaysBeforeClass)}
          style={{ grid: { stroke: 'none' } }}
          tickFormat={(tick, index) =>
            intl.formatMessage(messages.directBookingsDaysBeforeTick, { days: DIRECT_BOOKINGS_CHART_DAYS - index })
          }
          tickValues={xTicks}
        />
        <VictoryAxis dependentAxis tickValues={yTicks} />
        {current.length > 0 && (
          <VictoryArea data={current} name="area" style={{ data: { fill: COLOR_CURRENT_AREA, stroke: 'none' } }} />
        )}
        {current.length > 0 && (
          <VictoryLine data={current} name="current" style={{ data: { stroke: COLOR_DIRECT, strokeWidth: 2 } }} />
        )}
        {pastAvg.length > 0 && (
          <VictoryLine data={pastAvg} name="pastAvg" style={{ data: { stroke: COLOR_GRAY50, strokeWidth: 2 } }} />
        )}
        {prediction.length > 0 && (
          <VictoryLine
            data={prediction}
            name="prediction"
            style={{ data: { stroke: COLOR_DIRECT, strokeDasharray: DASHED, strokeWidth: 2 } }}
          />
        )}
        <VictoryLine
          data={[
            { x: today.x, y: 0 },
            { x: today.x, y: yDomainMax },
          ]}
          name="today"
          style={{ data: { stroke: COLOR_GRAY70, strokeDasharray: DOTTED, strokeWidth: 1 } }}
        />
      </VictoryChart>
      <Message
        as="span"
        className={element('today')}
        message={messages.directBookingsToday}
        style={{ left: todayLeft }}
      />
    </div>
  );
}

DirectBookingsChart.propTypes = {
  chart: PropTypes.shape({
    current: PropTypes.arrayOf(chartPointPropType).isRequired,
    pastAvg: PropTypes.arrayOf(chartPointPropType).isRequired,
    prediction: PropTypes.arrayOf(chartPointPropType).isRequired,
    today: chartPointPropType.isRequired,
    yMax: PropTypes.number.isRequired,
  }).isRequired,
  muted: PropTypes.bool,
};

const DirectBookingsSection = ({ schedule }) => {
  const navigate = useNavigate();
  const venueId = useSelector(selectActiveVenueId);
  const fillBar = getScheduleListV2FillBar(schedule);
  const isIntegrated = fillBar.variant === SCHEDULE_LIST_V2_FILL_BAR_VARIANT.INTEGRATED;
  const isVisible = Boolean(schedule.smartspotEnabled) && isIntegrated;
  const { data: fillRate, isError, isLoading } = useScheduleFillRate(schedule.scheduleId, { enabled: isVisible });

  if (!isVisible) {
    return null;
  }

  const hasLoadError = isError || hasFillRateLoadError(fillRate);
  const chart = getDirectBookingsChart(fillRate, schedule, { directBooked: fillBar.directBooked, hasLoadError });
  const copyVariant = getDirectBookingsCopyVariant(fillRate, hasLoadError);
  const predictionTooltipName = `${PREDICTION_TOOLTIP_NAME_PREFIX}-${schedule.scheduleId}`;

  const handleCreateCampaign = () => navigate(makePath.campaignsAdd({ venueId }));
  const handleOpenSmartSpotSettings = () => {
    navigate(makePath.smartTools(venueId));
    scrollToTop();
  };

  const handleManageSmartSpot = () => {
    Segment.event(SEGMENT_MANAGE_SMARTSPOT_CLICKED, {
      venueId,
      scheduleId: schedule.scheduleId,
      origin: SEGMENT_ORIGIN,
    });
    handleOpenSmartSpotSettings();
  };

  const inlineLink = chunks => (
    <Button
      className={element('inline-link')}
      styled="inline-link"
      onClick={copyVariant === DIRECT_BOOKINGS_COPY.NO_HISTORY ? handleCreateCampaign : handleOpenSmartSpotSettings}>
      {chunks}
    </Button>
  );

  const legendItem = (key, message, accessory = null) => (
    <span className={element('legend-item')} data-cypress={`direct-bookings-legend-${key}`}>
      <span className={element('swatch', { [key]: true })} />
      <Message message={message} />
      {accessory}
    </span>
  );

  const predictionTooltip = (
    <>
      <TooltipToggle name={predictionTooltipName}>
        <Isvg className={element('tooltip-icon')} src={ICONS.infoGrey} />
      </TooltipToggle>
      <TooltipContent className={element('tooltip-content')} hoverable name={predictionTooltipName} place="top">
        <Message message={messages.directBookingsPredictionTooltip} />
      </TooltipContent>
    </>
  );

  return (
    <section className={block()} data-cypress="schedule-detail-v2-direct-bookings">
      <Message as="h3" className={element('title')} message={messages.directBookingsTitle} />
      {!isLoading && (
        <Message
          as="p"
          className={element('body')}
          data-cypress={`direct-bookings-copy-${copyVariant}`}
          message={BODY_MESSAGE_BY_VARIANT[copyVariant]}
          values={{
            b: chunks => <span className={element('body-emphasis')}>{chunks}</span>,
            link: inlineLink,
            strong: chunks => <strong>{chunks}</strong>,
          }}
        />
      )}
      <div className={element('legend-row')}>
        <Message as="span" className={element('legend-label')} message={messages.directBookingsLegendLabel} />
        {legendItem('past-avg', messages.directBookingsLegendPastAvg)}
        {chart.current.length > 0 && legendItem('current', messages.directBookingsLegendCurrent)}
        {chart.prediction.length > 0 &&
          legendItem('prediction', messages.directBookingsLegendPrediction, predictionTooltip)}
        {fillRate?.capacity != null && (
          <Message
            as="span"
            className={element('capacity')}
            message={messages.directBookingsClassCapacity}
            values={{
              count: fillRate.capacity,
              value: chunks => <span className={element('capacity-value')}>{chunks}</span>,
            }}
          />
        )}
      </div>
      {isLoading ? (
        <LoadingDots />
      ) : (
        <div className={element('chart-wrapper')}>
          <DirectBookingsChart chart={chart} muted={hasLoadError} />
          {hasLoadError && (
            <Message
              as="p"
              className={element('error')}
              data-cypress="schedule-detail-v2-direct-bookings-error"
              message={messages.directBookingsChartError}
            />
          )}
        </div>
      )}
      <div className={element('footer')}>
        <Button
          className={element('manage-smartspot')}
          data-cypress="schedule-detail-v2-manage-smartspot"
          styled="inline-link"
          onClick={handleManageSmartSpot}>
          <Message message={messages.directBookingsManageSmartSpot} />
        </Button>
      </div>
    </section>
  );
};

DirectBookingsSection.propTypes = {
  schedule: PropTypes.shape(schedulePropTypes).isRequired,
};

export default DirectBookingsSection;
